import { useEffect, useRef, useState, type DragEvent } from 'react';
import { Box, Button, Card, Group, Progress, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { UPLOAD_CLI_THRESHOLD_BYTES, type DataObject } from '@/api/objects';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';
import {
  addRoot,
  compareRoots,
  DuplicateRootError,
  invalidPaths,
  removeRoot,
  rootFromDirectoryInput,
  rootFromFile,
  rootsFromDataTransfer,
  shapeOf,
  type RootDiff,
  type RootSummary,
  type Staged,
  type StagedRoot,
} from '@/upload/tree';
import { useUpload, type UploadState } from '@/upload/useUpload';
import { ObjectStatusBadge } from './ObjectStatusBadge';

export interface UploadPanelProps {
  object: DataObject;
  /** Injected in tests; defaults to the real hook. */
  upload?: ReturnType<typeof useUpload>;
}

function FilePicker({ label, onPick, big }: { label: string; onPick: (f: File) => void; big?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        hidden
        data-testid="file-input"
        onChange={(e) => {
          const f = e.currentTarget.files?.[0];
          if (f) onPick(f);
          e.currentTarget.value = '';
        }}
      />
      <Button size={big ? 'md' : 'sm'} onClick={() => ref.current?.click()}>
        {label}
      </Button>
    </>
  );
}

/** React's input typings omit the non-standard attribute; spreading keeps the type check happy. */
const DIRECTORY_ATTRS = { webkitdirectory: '' };

/** One folder per pick (that is what `webkitdirectory` allows); picks accumulate as roots. */
function FolderPicker({ label, onPick, big }: { label: string; onPick: (r: StagedRoot | null) => void; big?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        hidden
        multiple
        {...DIRECTORY_ATTRS}
        data-testid="folder-input"
        onChange={(e) => {
          onPick(rootFromDirectoryInput(Array.from(e.currentTarget.files ?? [])));
          e.currentTarget.value = '';
        }}
      />
      <Button size={big ? 'md' : 'sm'} variant="default" onClick={() => ref.current?.click()}>
        {label}
      </Button>
    </>
  );
}

function DropZone({ onRoots }: { onRoots: (roots: StagedRoot[]) => void }) {
  const { t } = useTranslation('projects');
  const [over, setOver] = useState(false);
  const onDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setOver(false);
    onRoots(await rootsFromDataTransfer(e.dataTransfer.items));
  };
  return (
    <Box
      data-testid="drop-zone"
      role="group"
      aria-label={t('upload.dropHere')}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
      }}
      onDrop={(e) => void onDrop(e)}
      p="md"
      style={{ border: `2px dashed ${brand.border}`, borderRadius: 12, background: over ? brand.surface2 : brand.surface }}
    >
      <Text fz={14} c={brand.textMuted} ta="center">
        {t('upload.dropHere')}
      </Text>
    </Box>
  );
}

/** Pickers, the staged roots and the start button; shared by the idle and resume states. */
function Stage({
  staged,
  setStaged,
  startLabel,
  onStart,
}: {
  staged: Staged;
  setStaged: (s: Staged) => void;
  startLabel: string;
  onStart: (s: Staged) => void;
}) {
  const { t } = useTranslation('projects');
  const { bytes } = useFormat();
  const [error, setError] = useState<string | null>(null);
  const bad = invalidPaths(staged);

  // A drop resolves asynchronously; build on the latest staging, not the one from the render that started it.
  // The parent changes `staged` only together with a `key` remount, so the ref needs no re-sync.
  const current = useRef(staged);
  // A drop that resolves after a remount (start over) must not stage into the new state.
  const alive = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const commit = (next: Staged) => {
    current.current = next;
    setStaged(next);
  };

  const addAll = (roots: (StagedRoot | null)[]) => {
    if (!alive.current) return;
    let next = current.current;
    let duplicate: string | null = null;
    for (const r of roots) {
      if (!r) continue;
      try {
        next = addRoot(next, r);
      } catch (e) {
        if (!(e instanceof DuplicateRootError)) throw e;
        duplicate ??= e.rootName;
      }
    }
    setError(duplicate === null ? null : t('upload.duplicateRoot', { name: duplicate }));
    commit(next);
  };

  return (
    <Stack gap="sm">
      <Group>
        <FilePicker big label={t('upload.pick')} onPick={(f) => addAll([rootFromFile(f)])} />
        <FolderPicker big label={t('upload.addFolder')} onPick={(r) => addAll([r])} />
      </Group>
      <DropZone onRoots={addAll} />
      {error && (
        <Text fz={14} c="red" role="alert">
          {error}
        </Text>
      )}
      {staged.roots.map((r) => (
        <Group key={r.name} justify="space-between">
          <Text fz={14} fw={600}>
            {r.name}
          </Text>
          <Group gap="sm">
            <Text fz={14} c={brand.textMuted}>
              {t('upload.fileCount', { count: r.files.length })} · {bytes(r.bytes)}
            </Text>
            <Button size="xs" variant="subtle" onClick={() => commit(removeRoot(current.current, r.name))}>
              {t('upload.remove')}
            </Button>
          </Group>
        </Group>
      ))}
      {bad.length > 0 && (
        <Text fz={14} c="red" role="alert">
          {t('upload.invalidPaths')}
        </Text>
      )}
      <Group>
        <Button disabled={shapeOf(staged) === 'empty' || bad.length > 0} onClick={() => onStart(staged)}>
          {startLabel}
        </Button>
      </Group>
    </Stack>
  );
}

/** The roots of the interrupted upload, each ticked once a matching root is staged again. */
function RootChecklist({ roots, staged }: { roots: RootSummary[]; staged: Staged }) {
  const { t } = useTranslation('projects');
  const { bytes } = useFormat();
  const diff = compareRoots(roots, staged);
  return (
    <Stack gap={4}>
      <Text fz={14}>{t('upload.resumeTreePick')}</Text>
      {roots.map((r) => {
        const status = diff.missing.includes(r.name) ? 'missing' : diff.changed.includes(r.name) ? 'differs' : 'picked';
        return (
          <Group key={r.name} gap="xs" wrap="nowrap" data-testid="resume-root" data-status={status}>
            <Text
              fz={14}
              fw={700}
              w={16}
              ta="center"
              c={status === 'picked' ? 'forest.6' : status === 'differs' ? 'red' : brand.textMuted}
              aria-label={status === 'picked' ? t('upload.rootPicked') : status === 'differs' ? t('upload.rootDiffers') : undefined}
            >
              {status === 'picked' ? '✓' : status === 'differs' ? '≠' : '•'}
            </Text>
            <Text fz={14} fw={600}>
              {r.name}
            </Text>
            <Text fz={14} c={brand.textMuted}>
              {t(r.kind === 'folder' ? 'upload.rootFolder' : 'upload.rootFile')} · {t('upload.fileCount', { count: r.fileCount })} · {bytes(r.bytes)}
            </Text>
          </Group>
        );
      })}
    </Stack>
  );
}

function MismatchDetails({ mismatch }: { mismatch: RootDiff }) {
  const { t } = useTranslation('projects');
  const lines = [
    ['upload.mismatchMissing', mismatch.missing],
    ['upload.mismatchExtra', mismatch.extra],
    ['upload.mismatchChanged', mismatch.changed],
  ] as const;
  return (
    <Stack gap={2}>
      {lines
        .filter(([, names]) => names.length > 0)
        .map(([key, names]) => (
          <Text key={key} fz={14} c="red">
            {t(key, { names: names.join(', ') })}
          </Text>
        ))}
    </Stack>
  );
}

function ProgressBlock({ state }: { state: UploadState }) {
  const { t } = useTranslation('projects');
  const { bytes, number } = useFormat();
  const pct = state.progress.total ? (state.progress.done / state.progress.total) * 100 : 0;
  return (
    <Stack gap={6}>
      <Group justify="space-between">
        <Text fz={14} fw={600}>
          {t(`upload.${state.phase}`)}
        </Text>
        <Text fz={14} c={brand.textMuted}>
          {bytes(state.progress.done)} / {bytes(state.progress.total)} · {number(pct)}{' '}%
        </Text>
      </Group>
      <Progress value={pct} color="forest.6" bg="forest.0" size="lg" radius="sm" animated={state.phase === 'verifying'} />
    </Stack>
  );
}

/** The upload surface for one object: stage a file or folders, hash, PUT, verify, result (D14, D8). */
export function UploadPanel({ object, upload }: UploadPanelProps) {
  const { t } = useTranslation('projects');
  const { bytes } = useFormat();
  const real = useUpload(object.public_id);
  const u = upload ?? real;
  const { state, resumable } = u;
  const [staged, setStaged] = useState<Staged>({ roots: [] });
  const [stageKey, setStageKey] = useState(0);
  const startOver = () => {
    u.cancel();
    setStaged({ roots: [] });
    setStageKey((k) => k + 1);
  };

  const verified = object.state !== 'pending' && object.state !== 'uploading';
  const busy = state.phase === 'hashing' || state.phase === 'opening' || state.phase === 'uploading' || state.phase === 'verifying';

  const start = (s: Staged) => {
    if (shapeOf(s) === 'file') void u.start(s.roots[0].files[0].file);
    else void u.startTree(s);
  };

  const resumeBlock = resumable && (
    <Stack gap="sm">
      {resumable.shape === 'file' ? (
        <>
          <Text fw={600}>{t('upload.resume', { name: resumable.fileName })}</Text>
          <Text fz={14} c={brand.textMuted}>
            {bytes(resumable.size)}
          </Text>
          <Group>
            <FilePicker label={t('upload.pickSame')} onPick={(f) => void u.resume(f)} />
            <Button variant="default" onClick={startOver}>
              {t('upload.startOver')}
            </Button>
          </Group>
        </>
      ) : (
        <>
          <Text fw={600}>{t('upload.resumeTree')}</Text>
          <Text fz={14} c={brand.textMuted}>
            {t('upload.fileCount', { count: resumable.fileCount })} · {bytes(resumable.totalBytes)} ·{' '}
            {resumable.roots.length > 0 ? t('upload.resumeTreeSkip') : t('upload.resumeTreeHint')}
          </Text>
          {resumable.arrived && resumable.arrived.fileCount > 0 && (
            <Text fz={14} c={brand.textMuted}>
              {t('upload.arrived', {
                files: resumable.arrived.fileCount,
                total: resumable.fileCount,
                bytes: bytes(resumable.arrived.bytes),
                totalBytes: bytes(resumable.totalBytes),
              })}
            </Text>
          )}
          {resumable.roots.length > 0 && <RootChecklist roots={resumable.roots} staged={staged} />}
          <Stage key={stageKey} staged={staged} setStaged={setStaged} startLabel={t('upload.continueTree')} onStart={(s) => void u.resumeTree(s)} />
          <Group>
            <Button variant="default" onClick={startOver}>
              {t('upload.startOver')}
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );

  return (
    <Card radius="xl" shadow="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Text fz={18} fw={600}>
            {t('upload.title')}
          </Text>
          <ObjectStatusBadge object={state.result ?? object} />
        </Group>

        {state.phase === 'done' || (verified && state.phase === 'idle') ? (
          <Stack gap={4}>
            <Text fw={600}>{t('upload.done')}</Text>
            <Text fz={14} c={brand.textMuted}>
              {t('upload.doneHint')}
            </Text>
          </Stack>
        ) : busy ? (
          <>
            <ProgressBlock state={state} />
            <Text fz={12} c={brand.textMuted}>
              {t('upload.keepOpen')}
            </Text>
            {state.progress.total > UPLOAD_CLI_THRESHOLD_BYTES && (
              <Text fz={12} c={brand.textMuted}>
                {t('upload.cliHint')}
              </Text>
            )}
            <Group justify="flex-end">
              <Button variant="default" size="xs" onClick={u.cancel}>
                {t('upload.cancel')}
              </Button>
            </Group>
          </>
        ) : state.phase === 'failed' ? (
          <Stack gap="sm">
            <Text fw={600} c="red">
              {t('upload.failed')}
            </Text>
            <Text fz={14} c={brand.textMuted} role="alert">
              {state.error === 'different_file'
                ? t('upload.differentFile')
                : state.error === 'different_tree'
                  ? t('upload.differentTree')
                  : t(`upload.reasons.${state.error ?? ''}`, { defaultValue: state.error ?? '' })}
            </Text>
            {state.error === 'different_tree' && state.mismatch && <MismatchDetails mismatch={state.mismatch} />}
            {resumable ? resumeBlock : <Stage key={stageKey} staged={staged} setStaged={setStaged} startLabel={t('upload.retry')} onStart={start} />}
          </Stack>
        ) : resumable ? (
          resumeBlock
        ) : (
          <Stack gap="sm">
            {object.last_failure && (
              <Text fz={14} c="red" role="alert">
                {t(`upload.reasons.${object.last_failure}`, { defaultValue: object.last_failure })}
              </Text>
            )}
            <Text fz={14} c={brand.textMuted}>
              {t('upload.keepOpen')}
            </Text>
            <Stage key={stageKey} staged={staged} setStaged={setStaged} startLabel={t('upload.start')} onStart={start} />
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
